import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SwarmIconClasses } from '../../../../enums';
import { NotificationBorderVariants, NotificationLevel } from '../../../enums';
import InformationTooltip from '../../../reactForm/InformationTooltip';
import { useHover } from '../utils';

function NotificationDetail({ level, swarmType, data, isLong, isLegacy }) {
    const { t } = useTranslation();
    const [showDescription, setShowDescription] = useState(false);
    const [isHovering, hoverProps] = useHover();
    const borderClass = useMemo(() => NotificationBorderVariants[level], [level]);

    const tooltip = isLegacy && isHovering && (
        <InformationTooltip
            information={gettext('LEGACY_NOTIFICATION_INFO')}
            className="pl-2 text-form-text"
        />
    );

    function getHighlightClass(item) {
        if (item.includes(`(${t('NEW')})`)) return 'bg-green-200';
        if (item.includes(`(${t('REMOVED')})`)) return 'bg-red-200';
        if (item.includes(`(${t('CHANGED')})`)) return 'bg-yellow-200';
        return '';
    }

    function renderExpandedContent() {
        return (
            <div>
                {data.split('\n').map((item, idx) => (
                    <div key={idx}>
                        <span
                            className={getHighlightClass(item)}
                            dangerouslySetInnerHTML={{ __html: item }}
                        />
                        {idx === 0 && tooltip}
                        <br />
                    </div>
                ))}
            </div>
        );
    }

    function renderCollapsedContent() {
        return (
            <>
                <span>{data.split('\n')[0]}</span>
                <small className="pl-2">{gettext('CLICK_FOR_MODE_DETAILS')}</small>
                {tooltip}
            </>
        );
    }

    function renderContent() {
        if (!isLong) {
            return (
                <>
                    <span>{data}</span>
                    {tooltip}
                </>
            );
        }
        return showDescription ? renderExpandedContent() : renderCollapsedContent();
    }

    return (
        <div
            className="relative"
            onClick={() => setShowDescription((oldState) => !oldState)}
            {...hoverProps}
        >
            <div
                className={`items-top flex gap-2 border-l-4 p-2.5 ${borderClass} ${
                    !showDescription && isLong ? 'collapsed' : ''
                }`}
            >
                <div className="w-[2%]">
                    <div
                        className={`mx-0 my-auto h-8 w-full ${SwarmIconClasses[swarmType]} bg-contain bg-center bg-no-repeat text-center text-8xl leading-7 text-gray-700`}
                    />
                </div>
                <div className="flex w-full items-center text-left">
                    <div>{renderContent()}</div>
                </div>
            </div>
        </div>
    );
}

NotificationDetail.propTypes = {
    id: PropTypes.any,
    level: PropTypes.oneOf(Object.values(NotificationLevel)).isRequired,
    swarmType: PropTypes.string.isRequired,
    data: PropTypes.string.isRequired,
    isLong: PropTypes.bool.isRequired,
    createdAt: PropTypes.string.isRequired,
    isLegacy: PropTypes.bool.isRequired,
};

export default NotificationDetail;
